import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { getSmsTypesData } from 'redux/reducers/sms/selectors';
import { TSmsTypeItem } from 'redux/reducers/sms/types';

const uniqueByHandle = (items: TSmsTypeItem[]): TSmsTypeItem[] => {
	const seen = new Set<string>();
	return items.filter((item) => {
		if (seen.has(item.handle)) return false;
		seen.add(item.handle);
		return true;
	});
};

export const SmsTypeHandleFilter = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const navigateParams = queryString.parse(location.search);
	const smsTypes = useSelector(getSmsTypesData);

	const options = useMemo(() => uniqueByHandle(smsTypes ?? []), [smsTypes]);

	const descriptionCounts = useMemo(() => {
		const counts = new Map<string, number>();
		options.forEach((item) => {
			const key = item.description ?? item.handle;
			counts.set(key, (counts.get(key) ?? 0) + 1);
		});
		return counts;
	}, [options]);

	const selectedHandle = navigateParams.SearchTypeHandle as string | undefined;
	const selectedOption: TSmsTypeItem | null =
		selectedHandle && smsTypes?.length
			? (smsTypes.find((t) => t.handle === selectedHandle) ?? null)
			: null;

	const handleChange = (_: unknown, value: TSmsTypeItem | null) => {
		const next = { ...navigateParams };
		if (value?.handle) {
			next.SearchTypeHandle = value.handle;
		} else {
			delete next.SearchTypeHandle;
		}
		navigate({ pathname: location.pathname, search: queryString.stringify(next) });
	};

	return (
		<Autocomplete<TSmsTypeItem, false>
			size="small"
			sx={{
				width: '400px',
				mr: 1,
				'& .MuiOutlinedInput-notchedOutline': {
					border: '1px solid #016193',
				},
			}}
			options={options}
			getOptionLabel={(option) => {
				const base = option.description ?? option.handle;
				const count = descriptionCounts.get(base) ?? 0;
				return count > 1 ? `${base} (${option.handle})` : base;
			}}
			value={selectedOption}
			onChange={handleChange}
			isOptionEqualToValue={(option, value) => option.handle === value?.handle}
			clearOnBlur={false}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="outlined"
					sx={{
						ml: 1,
						flex: 1,
						fontSize: '.9rem',
						height: '28px',
						p: '0px 0px',
						'& input': {
							padding: '0px 0px',
							margin: '0px',
							fontSize: '.8rem',
							height: '11px!important',
							color: '#016193',
							fontWeight: 'bold',
							'&::placeholder': {
								fontSize: '.8rem',
								fontWeight: 'normal',
							},
						},
					}}
					placeholder="Type"
					size="small"
				/>
			)}
		/>
	);
};
